import React, { useRef, useEffect, useState } from 'react';

import { Button, Group, Paper } from '@mantine/core';
import { 
  MDXEditor, 
  type MDXEditorMethods, 
  headingsPlugin, 
  listsPlugin, 
  quotePlugin, 
  thematicBreakPlugin, 
  markdownShortcutPlugin, 
  linkPlugin, 
  linkDialogPlugin, 
  tablePlugin, 
  codeBlockPlugin, 
  codeMirrorPlugin, 
  diffSourcePlugin, 
  frontmatterPlugin, 
  directivesPlugin, 
  AdmonitionDirectiveDescriptor,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  Separator
} from '@mdxeditor/editor';

export interface CarePlanEditorProps {
  initialContent?: string;
  onChange?: (content: string) => void;
  onSave?: (content: string) => void;
  mode?: 'add' | 'edit';
}

const CarePlanEditor: React.FC<CarePlanEditorProps> = ({
  initialContent = '',
  onChange,
  onSave,
  mode = 'add'
}) => {
  const editorRef = useRef<MDXEditorMethods>(null);
  const [currentContent, setCurrentContent] = useState(initialContent);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState(initialContent);

  // Update editor content when initialContent changes
  useEffect(() => {
    if (editorRef.current && initialContent) {
      editorRef.current.setMarkdown(initialContent);
      setCurrentContent(initialContent);
      setLastSavedContent(initialContent);
      setHasUnsavedChanges(false);
    }
  }, [initialContent]);

  // Handle content changes
  const handleContentChange = (content: string) => {
    setCurrentContent(content);
    setHasUnsavedChanges(content !== lastSavedContent);
    onChange?.(content);
  };

  // Handle save
  const handleSave = () => {
    onSave?.(currentContent);
    setLastSavedContent(currentContent);
    setHasUnsavedChanges(false);
  };

  // Handle beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        const message = 'You have unsaved changes to the care plan. Are you sure you want to leave?';
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  return (
    <Paper withBorder p="md">
      <div style={{ marginBottom: '16px' }}>
        <div style={{ border: '1px solid #e9ecef', borderRadius: '4px', minHeight: '300px' }}>
          <MDXEditor
            ref={editorRef}
            contentEditableClassName="prose"
            markdown={currentContent}
            plugins={[
              toolbarPlugin({
                toolbarContents: () => (
                  <>
                    <UndoRedo />
                    <Separator />
                    <BoldItalicUnderlineToggles />
                    <Separator />
                    <BlockTypeSelect />
                    <Separator />
                    <ListsToggle />
                    <Separator />
                    <CreateLink />
                    <InsertTable />
                    <InsertThematicBreak />
                  </>
                )
              }),
              headingsPlugin(),
              listsPlugin(),
              quotePlugin(),
              thematicBreakPlugin(),
              markdownShortcutPlugin(),
              linkPlugin(),
              linkDialogPlugin(),
              tablePlugin(),
              codeBlockPlugin({ defaultCodeBlockLanguage: 'txt' }),
              codeMirrorPlugin({ codeBlockLanguages: { txt: 'Plain Text', js: 'JavaScript', ts: 'TypeScript', css: 'CSS', html: 'HTML' } }),
              diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: '' }),
              frontmatterPlugin(),
              directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] })
            ]}
            onChange={handleContentChange}
          />
        </div>
      </div>
      
      {mode === 'edit' && (
        <Group gap="sm">
          <Button size="lg" onClick={handleSave}>
            Update Care Plan
          </Button>
        </Group>
      )}
    </Paper>
  );
};

export default CarePlanEditor;
